import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../components/ui/EmptyState.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { PillGroup, Pill, type PillTone } from '../components/PillGroup.js';
import { Button } from '../components/ui/Button.js';
import { Icon, type IconName } from '../components/Icon.js';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import {
  useLead,
  useLeadAnalysis,
  useLeadDrafts,
  useGenerateDrafts,
  usePauseOutreach,
  useResumeOutreach,
  useLeadReplies,
  useVerifyReply,
  useUpdateLead,
  useUpdateLeadStatus,
  useDeleteLead,
  useAnalyzeLead,
  useReEnrichLead,
  useReactivateLead,
  useRunLeadActions,
  useConvertLeadToContact,
  useLinkLeadToContact,
  useSendLeadEmail,
  type ConvertResult,
  type ReEnrichResult,
  type LeadRow,
  type OutreachDraft,
  type OutreachReply,
} from '../hooks/useLeads.js';
import { getLeadStatusMeta, getDisplayStage, getTerminalReason, type LeadStatusTone } from '../lib/leadStatusLabel.js';
import { parseServerDate } from '../lib/date.js';
import { useProjects } from '../hooks/useProjects.js';
import { CreateProjectModal } from '../components/projects/CreateProjectModal.js';
import { LeadClaudePanel } from '../components/leads/LeadClaudePanel.js';
import { LeadDraftCard } from '../components/leads/LeadDraftCard.js';
import { LeadReplyCard } from '../components/leads/LeadReplyCard.js';
import { htmlToText } from '../components/outreach/utils.js';
import { RichTextEditor } from '../components/ui/RichTextEditor.js';
import { SectionHeaderAction } from '../components/ui/SectionHeaderAction.js';
import { ActionDropdown, MultiActionDropdown, type ActionMenuItem, type MultiActionItem } from '../components/ui/Dropdown.js';
import { useToast } from '../components/ui/Toast.js';
import { Modal } from '../components/ui/Modal.js';
import { Input } from '../components/ui/Input.js';
import { IconButton } from '../components/ui/IconButton.js';
import { Pressable } from '../components/ui/Pressable.js';
import { useConfirm } from '../components/ui/ConfirmDialog.js';
import { useReports, useReport } from '../hooks/useReports.js';
import { ReportTypeSelector } from '../components/reports/ReportTypeSelector.js';
import { DetailSection } from '../components/ui/DetailSection.js';
import { Card, CardTitle } from '../components/ui/Card.js';
import { NotesModal } from '../components/leads/NotesModal.js';
import { KVRow as KVRowField } from '../components/ui/KVRow.js';
import { KVGrid } from '../components/ui/KVGrid.js';
import { Tabs, type TabItem } from '../components/ui/Tabs.js';

// ── Constants ──────────────────────────────────────────────────────────────────


const SOCIAL_NETS: Array<{ key: keyof LeadRow; label: string; icon: IconName }> = [
  { key: 'socialFacebook', label: 'Facebook', icon: 'social-facebook' },
  { key: 'socialInstagram', label: 'Instagram', icon: 'social-instagram' },
  { key: 'socialTwitter', label: 'Twitter', icon: 'social-twitter' },
  { key: 'socialLinkedin', label: 'LinkedIn', icon: 'social-linkedin' },
  { key: 'socialYoutube', label: 'YouTube', icon: 'social-youtube' },
  { key: 'socialTiktok', label: 'TikTok', icon: 'social-tiktok' },
];

// ── Helper types ───────────────────────────────────────────────────────────────

interface TechStack {
  hasJquery: boolean;
  jqueryVersion: string | null;
  hasBootstrap: boolean;
  bootstrapVersion: number | null;
  isHttps: boolean;
  isResponsive: boolean;
  generator: string | null;
  hasContactForm: boolean;
}

// ── Style constants ───────────────────────────────────────────────────────────

const LABEL_STYLE: React.CSSProperties = {
  fontSize: 'var(--evo-fs-caption)',
  fontWeight: 'var(--evo-fw-medium)',
  fontFamily: 'var(--evo-font)',
  letterSpacing: 'var(--evo-ls-caps)',
  textTransform: 'uppercase',
  color: 'var(--evo-fg-3)',
  margin: 0,
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseTechStack(s: string | null): TechStack | null {
  if (!s) return null;
  try { return JSON.parse(s) as TechStack; } catch { return null; }
}

function relTime(iso: string | null, t: (k: string, opts?: Record<string, unknown>) => string): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return t('common.timeAgo.justNow');
  if (mins < 60) return t('common.timeAgo.minutes', { count: mins });
  const h = Math.floor(mins / 60);
  return h < 24 ? t('common.timeAgo.hours', { count: h }) : t('common.timeAgo.days', { count: Math.floor(h / 24) });
}

// ── Primitives ────────────────────────────────────────────────────────────────

function CheckValue({ value }: { value: number | boolean | null | undefined }) {
  const { t } = useTranslation();
  if (value === null || value === undefined) return <span style={{ color: 'var(--evo-fg-3)' }}>—</span>;
  const on = value === 1 || value === true;
  return <span style={{ color: on ? 'var(--evo-cyan)' : 'var(--evo-danger)' }}>{on ? `✓ ${t('common.yes')}` : `✗ ${t('common.no')}`}</span>;
}

// ── Business snapshot ─────────────────────────────────────────────────────────

function BusinessSnapshot({ lead }: { lead: LeadRow }) {
  const { t } = useTranslation();
  const [imgError, setImgError] = useState(false);
  const updateLead = useUpdateLead();

  const favicon = lead.websiteUrl
    ? `https://www.google.com/s2/favicons?sz=80&domain_url=${encodeURIComponent(lead.websiteUrl)}`
    : null;
  const logoSrc = !imgError ? (lead.logoUrl ?? favicon) : null;

  const saveField = (field: string) => async (newValue: string) => {
    await updateLead.mutateAsync({ id: lead.id, [field]: newValue });
  };

  const emailValue = lead.contactEmail || lead.ownerEmail || lead.scrapedEmail || null;

  return (
    <div style={{ display: 'flex', gap: 'var(--evo-space-5)', alignItems: 'flex-start' }}>
        {logoSrc ? (
          <div style={{ width: '80px', height: '80px', borderRadius: 'var(--evo-r-md)', background: 'var(--evo-panel)', border: '1px solid var(--evo-stroke)', flexShrink: 0, overflow: 'hidden' }}>
            <img src={logoSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 'var(--evo-space-1)' }} onError={() => setImgError(true)} />
          </div>
        ) : (
          <div style={{ width: '48px', height: '48px', borderRadius: 'var(--evo-r)', background: 'var(--evo-panel)', border: '1px solid var(--evo-stroke)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ color: 'var(--evo-fg-3)', display: 'flex' }}>
              <Icon name="leads" size={20} />
            </span>
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <KVGrid columns={1}>
            <KVRowField
              label={t('leads.fieldPhone')}
              value={lead.phone}
              type="tel"
              onSave={saveField('phone')}
            />
            <KVRowField
              label={t('leads.fieldWeb')}
              value={lead.websiteUrl}
              type="url"
              renderValue={v => <a href={v} target="_blank" rel="noreferrer" style={{ color: 'var(--evo-cyan)' }}>{v.replace(/^https?:\/\//, '')}</a>}
              onSave={saveField('websiteUrl')}
            />
            <KVRowField
              label={t('leads.fieldEmail')}
              value={emailValue}
              type="email"
              renderValue={v => <a href={`mailto:${v}`} style={{ color: 'var(--evo-cyan)' }}>{v}</a>}
              onSave={saveField('contactEmail')}
            />
            <KVRowField
              label={t('leads.detail.fieldAddress')}
              value={lead.address}
              onSave={saveField('address')}
            />
            <KVRowField
              label={t('leads.fieldOwner')}
              value={lead.ownerName}
            />
            <KVRowField
              label={t('leads.fieldRating')}
              value={lead.rating !== null ? `${lead.rating} ★ · ${lead.reviewCount ?? 0} ${t('leads.reviews')}` : null}
              last
            />
          </KVGrid>
        </div>
      </div>
  );
}

// ── Left column sections ──────────────────────────────────────────────────────

function SummarySection({ summary }: { summary: string }) {
  return (
    <p style={{ fontSize: 'var(--evo-fs-body-lg)', lineHeight: 1.65, fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-2)', margin: 0 }}>
      {summary}
    </p>
  );
}

function ProposalAngleSection({ angle }: { angle: string }) {
  return (
    <div style={{ background: 'var(--evo-bg-2)', borderLeft: '3px solid var(--evo-cyan)', borderRadius: '0 6px 6px 0', padding: '20px 24px' }}>
      <p style={{ fontSize: 'var(--evo-fs-body-lg)', fontWeight: 'var(--evo-fw-medium)', lineHeight: 1.55, fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-1)', margin: 0 }}>
        {angle}
      </p>
    </div>
  );
}

function OpportunitiesSection({ opportunities }: { opportunities: string[] }) {
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {opportunities.map((opp, i) => (
          <li key={i} style={{
            display: 'flex',
            gap: '12px',
            padding: '16px 0',
            borderBottom: i < opportunities.length - 1 ? '1px solid var(--evo-stroke)' : 'none',
          }}>
            <span style={{ color: 'var(--evo-cyan)', flexShrink: 0, fontSize: '18px', lineHeight: 1.3, marginTop: '1px' }}>›</span>
            <p style={{ fontSize: 'var(--evo-fs-body-lg)', lineHeight: 1.55, fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-2)', margin: 0 }}>
              {opp}
            </p>
          </li>
        ))}
      </ul>
  );
}

// ── Right column sections ─────────────────────────────────────────────────────

function SuggestedServicesSection({ services }: { services: string[] }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
      {services.map(svc => (
        <div key={svc} style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)', padding: '12px 16px' }}>
          <p style={{ fontSize: 'var(--evo-list-title-fs)', fontWeight: 'var(--evo-fw-medium)', fontFamily: 'var(--evo-font)', color: 'var(--evo-cyan)', margin: 0 }}>
            {t(`leads.opportunity.type.${svc}`, { defaultValue: svc })}
          </p>
        </div>
      ))}
    </div>
  );
}

function TechStackSection({ lead }: { lead: LeadRow }) {
  const { t } = useTranslation();
  const tech = parseTechStack(lead.techStack);
  const obsoletePct = lead.techObsoleteScore !== null && lead.techObsoleteScore !== undefined
    ? Math.min(100, lead.techObsoleteScore)
    : null;
  const obsoleteColor = (obsoletePct ?? 0) >= 50 ? 'var(--evo-amber)' : 'var(--evo-cyan)';
  const noConditionals = lead.websiteAgeYears == null && !tech?.generator;

  return (
    <>
      <KVGrid columns={1}>
        <KVRowField
          label={t('leads.detail.techStack.ssl')}
          value={<CheckValue value={lead.hasSsl} />}
        />
        <KVRowField
          label={t('leads.detail.techStack.responsive')}
          value={<CheckValue value={lead.isResponsive} />}
        />
        <KVRowField
          label={t('leads.detail.techStack.contactForm')}
          value={<CheckValue value={lead.hasContactForm} />}
          {...(noConditionals && obsoletePct === null ? { last: true } : {})}
        />
        {lead.websiteAgeYears != null && (
          <KVRowField
            label={t('leads.detail.techStack.siteAge')}
            value={t('leads.detail.techStack.ageYears', { count: Number(lead.websiteAgeYears.toFixed(1)) })}
            {...(!tech?.generator && obsoletePct === null ? { last: true } : {})}
          />
        )}
        {tech?.generator && (
          <KVRowField
            label={t('leads.detail.techStack.generator')}
            value={tech.generator}
            {...(obsoletePct === null ? { last: true } : {})}
          />
        )}
      </KVGrid>
      {obsoletePct !== null && (
        <div style={{ padding: 'var(--evo-detail-kv-row-py) 0', borderTop: '1px solid var(--evo-stroke)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--evo-space-1)' }}>
            <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-text-muted)', fontFamily: 'var(--evo-font)' }}>
              {t('leads.detail.techStack.obsoleteScore')}
            </span>
            <span style={{ fontSize: 'var(--evo-fs-label)', color: obsoleteColor, fontFamily: 'var(--evo-font)', fontWeight: 'var(--evo-fw-semibold)' }}>
              {obsoletePct}/100
            </span>
          </div>
          <div style={{ height: '4px', borderRadius: 'var(--evo-r-pill)', background: 'var(--evo-stroke)' }}>
            <div style={{ width: `${obsoletePct}%`, height: '100%', borderRadius: 'var(--evo-r-pill)', background: obsoleteColor }} />
          </div>
        </div>
      )}
    </>
  );
}

function SocialPresenceSection({ lead }: { lead: LeadRow }) {
  const { t } = useTranslation();
  const updateLead = useUpdateLead();
  const [adding, setAdding] = useState<{ key: keyof LeadRow; label: string } | null>(null);
  const [urlInput, setUrlInput] = useState('');

  function openAdd(key: keyof LeadRow, label: string) {
    setAdding({ key, label });
    setUrlInput('');
  }

  function saveUrl() {
    if (!adding || !urlInput.trim()) return;
    const payload: { id: string } & Record<string, string> = { id: lead.id };
    payload[String(adding.key)] = urlInput.trim();
    updateLead.mutate(payload, { onSuccess: () => setAdding(null) });
  }

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
        {SOCIAL_NETS.map(({ key, label, icon }) => {
          const url = lead[key] as string | null | undefined;
          const isActive = !!url;
          return (
            <IconButton
              key={String(key)}
              icon={<Icon name={icon} size={16} strokeWidth={1.5} />}
              ariaLabel={label}
              tooltip={isActive ? label : t('leads.detail.social.addTitle', { network: label })}
              variant="outline"
              tone={isActive ? 'cyan' : 'muted'}
              size="md"
              shape="circle"
              onClick={() =>
                isActive
                  ? window.open(url, '_blank', 'noopener,noreferrer')
                  : openAdd(key, label)
              }
            />
          );
        })}
      </div>

      {adding && (
        <Modal
          open
          onClose={() => setAdding(null)}
          size="sm"
          title={t('leads.detail.social.addTitle', { network: adding.label })}
          footer={
            <>
              <Button
                variant="ghost"
                tone="muted"
                size="sm"
                onClick={() => setAdding(null)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="solid"
                tone="cyan"
                size="sm"
                loading={updateLead.isPending}
                disabled={!urlInput.trim()}
                onClick={saveUrl}
              >
                {t('leads.detail.social.addBtn')}
              </Button>
            </>
          }
        >
          <Input
            type="url"
            value={urlInput}
            onChange={setUrlInput}
            onKeyDown={e => {
              if (e.key === 'Enter') saveUrl();
            }}
            placeholder={t('leads.detail.social.addPlaceholder')}
            autoFocus
            fullWidth
          />
        </Modal>
      )}
    </>
  );
}

function NotesSection({ lead }: { lead: LeadRow }) {
  function notesToHtml(content: string): string {
    if (/<(p|br|ul|ol|li|strong|em|u|s|a|blockquote|code)[\s/>]/i.test(content)) {
      return content;
    }
    const escaped = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return `<p>${escaped.replace(/\n/g, '<br/>')}</p>`;
  }

  if (!lead.notes?.trim()) return null;

  return (
    <div
      className="evo-rte-content"
      dangerouslySetInnerHTML={{ __html: notesToHtml(lead.notes ?? '') }}
    />
  );
}

const PROJECT_STATUS_TONES: Record<string, PillTone> = {
  draft:            'neutral',
  briefed:          'blue',
  content_planning: 'cyan',
  in_design:        'cyan',
  client_review: 'amber',
  revisions:     'warn',
  approved:      'success',
  delivered:     'info',
  archived:      'muted',
};

function LeadProjectsTab({ lead }: { lead: LeadRow }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { data, isLoading } = useProjects({ leadId: lead.id });
  const convertToContact = useConvertLeadToContact();
  const items = data?.items ?? [];
  const [modalOpen, setModalOpen] = useState(false);
  const [preselectedContact, setPreselectedContact] = useState<{ id: number; name: string } | null>(null);

  function openModal(contactId: number) {
    const name = lead.businessName || lead.ownerName || String(contactId);
    setPreselectedContact({ id: contactId, name });
    setModalOpen(true);
  }

  function handleNewProject() {
    if (lead.contactId !== null) {
      openModal(lead.contactId);
      return;
    }
    convertToContact.mutate({ id: lead.id }, {
      onSuccess: (result: ConvertResult) => openModal(result.contactId),
      onError: (err: Error) => toast.error(err.message || t('common.error')),
    });
  }

  return (
    <div style={{ maxWidth: '1200px', paddingTop: 'var(--evo-space-6)' }}>
      <DetailSection
        title={t('leads.detail.tabs.projects')}
        divider={false}
        titleRight={
          <SectionHeaderAction
            loading={convertToContact.isPending}
            onClick={handleNewProject}
          >
            {t('projects.newProject')}
          </SectionHeaderAction>
        }
      >
        {isLoading && <LoadingState size="sm" />}
        {!isLoading && items.length === 0 && <EmptyState size="sm" title={t('projects.empty')} />}
        {!isLoading && items.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
            {items.map(p => (
              <Pressable key={p.id} onPress={() => navigate(`/proyectos/${p.slug}`)} fullWidth>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 10px', borderRadius: 'var(--evo-r)',
                  border: '1px solid var(--evo-stroke)', gap: '8px',
                }}>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.name}
                  </span>
                  <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', flexShrink: 0 }}>
                    {t(`projects.type.${p.type}`, { defaultValue: p.type })}{p.subtype ? ` · ${t(`projects.subtype.${p.subtype}`, { defaultValue: p.subtype })}` : ''}
                  </span>
                  <Pill tone={PROJECT_STATUS_TONES[p.status] ?? 'neutral'} variant="subtle" size="sm">
                    {t(`projects.status.${p.status}`, { defaultValue: p.status })}
                  </Pill>
                </div>
              </Pressable>
            ))}
          </div>
        )}
      </DetailSection>
      <CreateProjectModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        preselectedContact={preselectedContact}
        leadId={lead.id}
      />
    </div>
  );
}

function statusToneToPillTone(tone: LeadStatusTone): PillTone {
  switch (tone) {
    case 'info':    return 'info';
    case 'success': return 'cyan';
    case 'warn':    return 'amber';
    case 'danger':  return 'danger';
    case 'muted':   return 'muted';
    default:        return 'neutral';
  }
}

// ── SubNavBar ─────────────────────────────────────────────────────────────────

function SubNavBar({ lead, analysis, tabs, activeTab, onTabChange }: {
  lead: LeadRow;
  analysis?: { analyzedAt?: string | null; costUsd?: number | null; language?: string | null } | null;
  tabs: TabItem<string>[];
  activeTab: string;
  onTabChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const locState = (location.state as { filteredIds?: string[]; filters?: Record<string, unknown> } | null) ?? {};
  const filteredIds: string[] = locState.filteredIds ?? [];
  const currentIndex = filteredIds.indexOf(lead.id);
  const prevId = currentIndex > 0 ? filteredIds[currentIndex - 1] : null;
  const nextId = currentIndex < filteredIds.length - 1 ? filteredIds[currentIndex + 1] : null;
  const showNav = filteredIds.length > 1 && currentIndex >= 0;

  function navTo(id: string) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- location.state is typed `any` by React Router v6; forwarded as-is to preserve navigation state
    navigate(`/leads/${id}`, { state: location.state });
  }

  function goBack() {
    const filtersState = locState.filters;
    if (filtersState) {
      const params = new URLSearchParams();
      if (filtersState['search']) params.set('search', String(filtersState['search']));
      if (filtersState['pipelineStage']) params.set('pipelineStage', String(filtersState['pipelineStage']));
      if (filtersState['leadCategory']) params.set('leadCategory', String(filtersState['leadCategory']));
      if (filtersState['city']) params.set('city', String(filtersState['city']));
      if (filtersState['hasWebsite'] !== undefined) params.set('hasWebsite', String(filtersState['hasWebsite']));
      if (filtersState['hasEmail'] !== undefined) params.set('hasEmail', String(filtersState['hasEmail']));
      if (filtersState['hasDueDrafts']) params.set('hasDueDrafts', 'true');
      const qs = params.toString();
      navigate(qs ? `/leads?${qs}` : '/leads');
    } else {
      navigate('/leads');
    }
  }

  const analysisMeta = analysis?.analyzedAt
    ? `${relTime(analysis.analyzedAt, t as (k: string, opts?: Record<string, unknown>) => string)} · $${(analysis.costUsd ?? 0).toFixed(3)} USD · ${(analysis.language ?? '—').toUpperCase()}`
    : null;

  return (
    <div style={{
      height: 'var(--evo-subnav-h)',
      borderBottom: '1px solid var(--evo-stroke)',
      flexShrink: 0,
      background: 'var(--evo-bg)',
    }}>
      <div style={{ padding: '0 var(--evo-content-pad-x)', height: '100%' }}>
      <div style={{ maxWidth: '1200px', height: '100%', display: 'flex', alignItems: 'stretch' }}>
      {/* Left: back + separator + status pill */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0, paddingRight: '16px' }}>
        <Button
          variant="ghost"
          tone="muted"
          size="sm"
          icon={<span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}><Icon name="arrow-right" size={12} /></span>}
          onClick={goBack}
        >
          {t('leads.detail.back')}
        </Button>
        <div style={{ width: '1px', height: '16px', background: 'var(--evo-stroke)', flexShrink: 0 }} />
        {/* Status pill */}
        {(() => {
          const terminalReason = getTerminalReason(lead);
          const meta = getLeadStatusMeta(getDisplayStage(lead));
          const label = terminalReason
            ? (terminalReason.fallbackKey
                ? t(terminalReason.labelKey, { defaultValue: t(terminalReason.fallbackKey) })
                : t(terminalReason.labelKey))
            : t(meta.labelKey);
          const pillTone: PillTone = terminalReason
            ? (terminalReason.colorVar === '--evo-danger' ? 'danger' : 'neutral')
            : statusToneToPillTone(meta.tone);
          return (
            <Pill variant="outline" tone={pillTone} size="sm">
              {label}
            </Pill>
          );
        })()}
      </div>

      {/* Tabs: fill remaining space, centered horizontally */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', overflow: 'hidden', minWidth: 0 }}>
        <Tabs
          tabs={tabs}
          value={activeTab}
          onChange={onTabChange}
          size="md"
          noBorder
        />
      </div>

      {/* Right: Prev/Next navigation */}
      {showNav ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, paddingLeft: 'var(--evo-content-pad-x)' }}>
          <Button
            variant="outline"
            tone="muted"
            size="sm"
            disabled={!prevId}
            icon={<span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}><Icon name="arrow-right" size={11} /></span>}
            onClick={() => prevId && navTo(prevId)}
          >
            {t('leads.detail.prev')}
          </Button>
          <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)', whiteSpace: 'nowrap', minWidth: '52px', textAlign: 'center' }}>
            {t('leads.detail.navPosition', { current: currentIndex + 1, total: filteredIds.length })}
          </span>
          <Button
            variant="outline"
            tone="muted"
            size="sm"
            disabled={!nextId}
            iconAfter={<Icon name="arrow-right" size={11} />}
            onClick={() => nextId && navTo(nextId)}
          >
            {t('leads.detail.next')}
          </Button>
        </div>
      ) : null}
      </div>
      </div>
    </div>
  );
}

// ── Outreach Section ─────────────────────────────────────────────────────────

type TLItem =
  | { kind: 'draft'; data: OutreachDraft; ts: number }
  | { kind: 'reply'; data: OutreachReply; ts: number };


function OutreachSection({ lead }: { lead: LeadRow }) {
  const { t } = useTranslation();
  const { data: drafts, isLoading } = useLeadDrafts(lead.id);
  const { data: replies } = useLeadReplies(lead.id);
  const generateDrafts = useGenerateDrafts();
  const pauseOutreach = usePauseOutreach();
  const resumeOutreach = useResumeOutreach();
  const verifyReply = useVerifyReply();
  const updateLead = useUpdateLead();
  const reEnrich = useReEnrichLead(lead.id);
  const hasAnalysis = lead.analysisStatus === 'completed';
  const hasEmail = !!(lead.contactEmail || lead.ownerEmail || lead.scrapedEmail);
  const [addingEmail, setAddingEmail] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailSavedMsg, setEmailSavedMsg] = useState('');
  const [reEnrichMsg, setReEnrichMsg] = useState<{ text: string; ok: boolean } | null>(null);

  const sendLeadEmail = useSendLeadEmail(lead.id);
  const [newEmailOpen, setNewEmailOpen] = useState(false);
  const [newEmailSubject, setNewEmailSubject] = useState('');
  const [newEmailBody, setNewEmailBody] = useState('');

  const hasPendingDrafts = drafts?.some(d => d.status === 'pending_review') ?? false;
  const hasPausedDrafts = drafts?.some(d => d.status === 'paused') ?? false;
  const visibleDrafts = (drafts ?? []).filter(d => d.status !== 'paused');

  const hasReplies = (replies?.length ?? 0) > 0;
  const lastInboundTs = (replies ?? [])
    .filter(r => r.direction !== 'outbound')
    .reduce((max, r) => Math.max(max, r.receivedAt), 0);
  const isDraftObsolete = (draft: OutreachDraft) =>
    draft.status === 'pending_review' && draft.isDue !== 1 && lastInboundTs > 0 && draft.createdAt < lastInboundTs;
  const timeline: TLItem[] = [
    ...visibleDrafts.map(d => ({ kind: 'draft' as const, data: d, ts: d.sentAt ?? d.scheduledFor ?? d.createdAt })),
    ...(replies ?? []).map(r => ({ kind: 'reply' as const, data: r, ts: r.receivedAt })),
  ].sort((a, b) => a.ts - b.ts);

  return (
    <div style={{ maxWidth: '1200px', paddingTop: 'var(--evo-space-6)' }}>
      <DetailSection
        title={t('leads.outreach.title')}
        titleRight={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {hasPendingDrafts && (
              <SectionHeaderAction
                tone="amber"
                loading={pauseOutreach.isPending}
                onClick={() => pauseOutreach.mutate(lead.id)}
              >
                {t('leads.outreach.pause')}
              </SectionHeaderAction>
            )}
            {!hasPendingDrafts && hasPausedDrafts && (
              <SectionHeaderAction
                loading={resumeOutreach.isPending}
                onClick={() => resumeOutreach.mutate(lead.id)}
              >
                {t('leads.outreach.resume')}
              </SectionHeaderAction>
            )}
            <SectionHeaderAction
              disabled={!hasAnalysis || !hasEmail || generateDrafts.isPending}
              onClick={() => generateDrafts.mutate(lead.id)}
            >
              {generateDrafts.isPending
                ? t('leads.outreach.generatingBtn')
                : drafts?.length
                ? t('leads.outreach.regenerateBtn')
                : t('leads.outreach.generateBtn')}
            </SectionHeaderAction>
            <SectionHeaderAction
              disabled={!hasEmail}
              onClick={() => setNewEmailOpen(v => !v)}
            >
              {t('leads.outreach.newEmail')}
            </SectionHeaderAction>
          </div>
        }
        divider={false}
      >

      {(pauseOutreach.isError || resumeOutreach.isError) && (
        <p style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-warn)', margin: '0 0 8px', fontFamily: 'var(--evo-font)' }}>
          {((pauseOutreach.error ?? resumeOutreach.error))?.message ?? t('leads.outreach.action_failed')}
        </p>
      )}

      {hasAnalysis && !hasEmail && (
        <div style={{ marginBottom: '12px', padding: '10px 14px', borderRadius: 'var(--evo-r)', background: 'rgba(245,158,11,0.08)', border: '1px solid var(--evo-warn)' }}>
          <p style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-warn)', fontFamily: 'var(--evo-font)', margin: '0 0 8px 0' }}>
            {t('leads.outreach.noEmail')}
          </p>
          {!addingEmail ? (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <Button variant="outline" tone="amber" size="sm" onClick={() => setAddingEmail(true)}>
                {t('leads.outreach.addEmail')}
              </Button>
              <Button
                variant="outline"
                tone="muted"
                size="sm"
                loading={reEnrich.isPending}
                disabled={reEnrich.isPending}
                onClick={() => {
                  setReEnrichMsg(null);
                  reEnrich.mutate(undefined, {
                    onSuccess: (result: ReEnrichResult) => {
                      if (result.status === 'found_website' || result.status === 'found_maps') {
                        const msg = result.email
                          ? t('leads.outreach.reEnrichFound', { email: result.email })
                          : t('leads.outreach.reEnrichFoundPhone', { phone: result.phone ?? '' });
                        setReEnrichMsg({ text: msg, ok: true });
                      } else {
                        setReEnrichMsg({ text: t('leads.outreach.reEnrichNotFound'), ok: false });
                      }
                    },
                  });
                }}
              >
                {t('leads.outreach.reEnrich')}
              </Button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <Input
                type="email"
                size="sm"
                value={emailInput}
                onChange={v => setEmailInput(v)}
                placeholder={t('leads.outreach.addEmailPlaceholder')}
              />
              <Button
                variant="outline"
                tone="cyan"
                size="sm"
                loading={updateLead.isPending}
                disabled={!emailInput || updateLead.isPending}
                onClick={() => updateLead.mutate(
                  { id: lead.id, contactEmail: emailInput },
                  {
                    onSuccess: () => {
                      setAddingEmail(false);
                      setEmailInput('');
                      setEmailSavedMsg(t('leads.outreach.addEmailSaved'));
                      setTimeout(() => setEmailSavedMsg(''), 2000);
                    },
                  }
                )}
              >
                {t('leads.outreach.addEmailSave')}
              </Button>
              <IconButton
                icon={<Icon name="close" size={12} />}
                ariaLabel="Cancel"
                variant="ghost"
                tone="muted"
                size="sm"
                onClick={() => { setAddingEmail(false); setEmailInput(''); }}
              />
            </div>
          )}
          {emailSavedMsg && <p style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-success)', fontFamily: 'var(--evo-font)', margin: '6px 0 0 0' }}>{emailSavedMsg}</p>}
          {reEnrichMsg && (
            <p style={{ fontSize: 'var(--evo-fs-caption)', color: reEnrichMsg.ok ? 'var(--evo-success)' : 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', margin: '6px 0 0 0' }}>
              {reEnrichMsg.text}
            </p>
          )}
        </div>
      )}
      {generateDrafts.isError && (
        <p style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-warn)', fontFamily: 'var(--evo-font)', margin: '0 0 8px 0' }}>
          {(generateDrafts.error)?.message ?? t('leads.outreach.drafts_failed')}
        </p>
      )}
      {!hasAnalysis && (
        <EmptyState size="sm" reserveSpace title={t('leads.outreach.emptyNoAnalysis')} />
      )}
      {hasAnalysis && isLoading && <LoadingState size="sm" />}
      {hasAnalysis && !isLoading && (!drafts || drafts.length === 0) && (
        <EmptyState size="sm" reserveSpace title={t('leads.outreach.empty')} />
      )}

      {/* Unified chronological timeline */}
      {timeline.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {timeline.map(item => {
            if (item.kind === 'reply') {
              return (
                <LeadReplyCard
                  key={item.data.id}
                  reply={item.data}
                  leadId={lead.id}
                  onVerify={() => verifyReply.mutate({ replyId: item.data.id, isRelevant: true, leadId: lead.id })}
                  onDismiss={() => verifyReply.mutate({ replyId: item.data.id, isRelevant: false, leadId: lead.id })}
                />
              );
            }
            return (
              <LeadDraftCard key={item.data.id} draft={item.data} leadId={lead.id} isObsolete={isDraftObsolete(item.data)} />
            );
          })}
          {(() => {
            const genCost = drafts?.[0]?.claudeCostUsd;
            if (genCost == null || hasReplies) return null;
            return (
              <p style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', margin: 0, textAlign: 'right' }}>
                {t('leads.outreach.generatedMeta', { cost: genCost.toFixed(4) })}
              </p>
            );
          })()}
        </div>
      )}

      {/* New email inline composer */}
      {newEmailOpen && (
        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '10px', border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r-md)', padding: '14px 16px' }}>
          <Input
            type="text"
            label={t('leads.outreach.newEmailSubject')}
            value={newEmailSubject}
            onChange={v => setNewEmailSubject(v)}
            fullWidth
          />
          <RichTextEditor
            value={newEmailBody}
            onChange={setNewEmailBody}
            placeholder={t('composer.editor.placeholder')}
            minHeight={160}
          />
          {sendLeadEmail.isError && (
            <p style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-warn)', fontFamily: 'var(--evo-font)', margin: 0 }}>
              {sendLeadEmail.error?.message ?? t('leads.outreach.action_failed')}
            </p>
          )}
          <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid var(--evo-stroke)', paddingTop: '10px' }}>
            <Button
              variant="solid"
              tone="cyan"
              size="sm"
              loading={sendLeadEmail.isPending}
              disabled={!newEmailSubject.trim() || !htmlToText(newEmailBody).trim() || sendLeadEmail.isPending}
              onClick={() => {
                const text = htmlToText(newEmailBody);
                if (!newEmailSubject.trim() || !text.trim()) return;
                sendLeadEmail.mutate(
                  { subject: newEmailSubject.trim(), bodyHtml: newEmailBody, bodyText: text },
                  { onSuccess: () => { setNewEmailOpen(false); setNewEmailSubject(''); setNewEmailBody(''); } },
                );
              }}
            >
              {t('leads.outreach.newEmailSend')}
            </Button>
            <Button
              variant="outline"
              tone="muted"
              size="sm"
              onClick={() => { setNewEmailOpen(false); setNewEmailSubject(''); setNewEmailBody(''); sendLeadEmail.reset(); }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      )}
      </DetailSection>
    </div>
  );
}

// ── Lead Reports Block ────────────────────────────────────────────────────────

function LeadReportsBlock({ leadId, leadName, websiteUrl }: { leadId: string; leadName: string; websiteUrl: string | null }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const { data } = useReports({ leadId });
  const reports = data?.reports ?? [];
  const [showSelector, setShowSelector] = useState(false);
  const [pollingId, setPollingId] = useState<number | null>(null);
  const { data: pollingReport } = useReport(pollingId);

  useEffect(() => {
    if (!pollingReport || !pollingId) return;
    if (pollingReport.status === 'ready') {
      toast.success(t('reports.selector.readyToast'));
      setPollingId(null);
      void qc.invalidateQueries({ queryKey: ['reports'] });
    } else if (pollingReport.status === 'failed') {
      toast.error(pollingReport.lastError ?? 'Error al generar');
      setPollingId(null);
    }
  }, [pollingReport?.status]);

  return (
    <div style={{ maxWidth: '1200px', paddingTop: 'var(--evo-space-6)' }}>
      {showSelector && (
        <div style={{ marginBottom: '12px' }}>
          <ReportTypeSelector
            leadId={leadId}
            leadName={leadName}
            initialUrl={websiteUrl}
            onClose={() => setShowSelector(false)}
            onCreated={(id) => setPollingId(id)}
          />
        </div>
      )}
      <DetailSection
        title={t('reports.leadBlock.title')}
        titleRight={
          <SectionHeaderAction
            onClick={() => setShowSelector(true)}
          >
            {t('reports.leadBlock.newBtn')}
          </SectionHeaderAction>
        }
        divider={false}
      >
      {reports.length === 0 ? (
        <p style={{ fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', margin: 0 }}>{t('reports.noReports')}</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
          {reports.map(r => {
            const REPORT_STATUS_TONES: Record<string, PillTone> = {
              ready:      'cyan',
              failed:     'danger',
              generating: 'info',
              pending:    'info',
            };
            return (
              <Pressable key={r.id} onPress={() => navigate(`/reports/${r.id}`)} fullWidth>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 10px', borderRadius: 'var(--evo-r)',
                  border: '1px solid var(--evo-stroke)', gap: '8px',
                }}>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.title}
                  </span>
                  <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', flexShrink: 0 }}>
                    {t(`reports.types.${r.type}.label`, { defaultValue: r.type })} · {parseServerDate(r.createdAt).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                  <Pill tone={REPORT_STATUS_TONES[r.status] ?? 'neutral'} variant="subtle" size="sm">
                    {t(`reports.status.${r.status}`, { defaultValue: r.status })}
                  </Pill>
                </div>
              </Pressable>
            );
          })}
        </div>
      )}
      </DetailSection>
    </div>
  );
}

// ── Actions Dropdown (multi-select + proceed) ────────────────────────────────
// Lifecycle actions for the lead (analyze → draft → send) are surfaced as
// checkboxes that queue and execute together via "Proceed", so the user can
// chain steps without dismissing the menu (especially useful right after
// reactivating a lead from junk/archived). Mark Won / Mark Lost / Convert to
// Contact are click-once and don't queue.

interface ActionsDropdownProps {
  lead: LeadRow;
  hasAnalysis: boolean;
  updateStatusPending: boolean;
  runActionsPending: boolean;
  onProceed: (actions: string[]) => void;
  onMarkWon: () => void;
  onMarkLost: () => void;
  onConvertToContact: () => void;
}

function ActionsDropdown({
  lead,
  hasAnalysis,
  updateStatusPending,
  runActionsPending,
  onProceed,
  onMarkWon,
  onMarkLost,
  onConvertToContact,
}: ActionsDropdownProps) {
  const { t } = useTranslation();

  const items: MultiActionItem[] = [
    {
      type: 'checkbox', key: 'analyze',
      label: hasAnalysis ? t('leads.actions.reanalyze') : t('leads.actions.analyze'),
    },
    {
      type: 'checkbox', key: 'draft',
      label: t('leads.actions.generateDraft'),
    },
    {
      type: 'checkbox', key: 'send',
      label: t('leads.actions.sendDraft'),
    },
    { type: 'separator' },
    {
      type: 'action', label: t('leads.actions.markWon'),
      onClick: onMarkWon,
      disabled: updateStatusPending || lead.status === 'closed_won',
    },
    {
      type: 'action', label: t('leads.actions.markLost'),
      onClick: onMarkLost,
      disabled: updateStatusPending || lead.status === 'closed_lost',
    },
    { type: 'separator' },
    {
      type: 'action',
      label: lead.contactId !== null ? t('leads.detail.viewContact') : t('leads.detail.convertToContact'),
      onClick: onConvertToContact,
      disabled: lead.contactId === null && !lead.contactEmail && !lead.ownerEmail && !lead.scrapedEmail,
      ...(lead.contactId === null && !lead.contactEmail && !lead.ownerEmail && !lead.scrapedEmail
        ? { title: t('leads.detail.convertNoEmail') }
        : {}),
    },
  ];

  return (
    <MultiActionDropdown
      label={t('leads.actions.actionsMenu')}
      items={items}
      onProceed={onProceed}
      proceedLabel={t('leads.actions.proceed')}
      proceedLoading={runActionsPending}
      size="sm"
      tone="muted"
    />
  );
}

// ── Remove Dropdown ──────────────────────────────────────────────────────────
// Destructive lifecycle transitions grouped together. Reactivate is rendered
// at the top of the menu only when the lead is currently in a terminal status
// (junk or archived) — it restores status='enriched' (or 'scraped' if never
// enriched), pipelineStage='new', and clears junk_reason so the normal
// pipeline can run again.

interface RemoveDropdownProps {
  lead: LeadRow;
  updateStatusPending: boolean;
  reactivatePending: boolean;
  onArchive: () => void;
  onJunk: () => void;
  onDelete: () => void;
  onReactivate: () => void;
}

function RemoveDropdown({
  lead, updateStatusPending, reactivatePending,
  onArchive, onJunk, onDelete, onReactivate,
}: RemoveDropdownProps) {
  const { t } = useTranslation();
  const isTerminal = lead.status === 'junk' || lead.status === 'archived';

  const items: ActionMenuItem[] = [];

  if (isTerminal) {
    items.push({
      type: 'action',
      label: t('leads.actions.reactivate'),
      onClick: onReactivate,
      disabled: reactivatePending,
    });
    items.push({ type: 'separator' });
  }

  items.push({
    type: 'action',
    label: t('leads.actions.markArchive'),
    onClick: onArchive,
    disabled: updateStatusPending || lead.status === 'archived',
  });
  items.push({
    type: 'action',
    label: t('leads.actions.markJunk'),
    onClick: onJunk,
    disabled: updateStatusPending || lead.status === 'junk',
  });
  items.push({ type: 'separator' });
  items.push({
    type: 'action',
    label: t('leads.deleteBtn'),
    onClick: onDelete,
  });

  return (
    <ActionDropdown
      label={t('leads.actions.removeMenu')}
      items={items}
      size="sm"
      tone={isTerminal ? 'cyan' : 'muted'}
    />
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setOverride } = useTopBarCtx();

  const { data: lead, isLoading } = useLead(id ?? null);
  const { data: analysis } = useLeadAnalysis(id ?? null);

  const updateStatus = useUpdateLeadStatus();
  const deleteLead = useDeleteLead();
  const analyze = useAnalyzeLead();
  const generateDrafts = useGenerateDrafts();
  const reactivateLead = useReactivateLead();
  const runLeadActions = useRunLeadActions();
  const convertToContact = useConvertLeadToContact();
  const linkToContact = useLinkLeadToContact();
  const toast = useToast();
  const { data: draftsForHeader } = useLeadDrafts(id ?? null);
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();
  const [activeTab, setActiveTab] = useState<'info' | 'outreach' | 'projects' | 'reports'>('info');
  const [convertMatch, setConvertMatch] = useState<{ contactId: number; email: string } | null>(null);
  const [notesOpen, setNotesOpen] = useState(false);

  useEffect(() => {
    if (!lead) return;
    const sub = [lead.category, lead.city].filter(Boolean).join(' · ');

    const handleProceed = (actions: string[]) => {
      runLeadActions.mutate(
        { id: lead.id, actions },
        {
          onSuccess: () => {
            toast.info(t('leads.actions.proceedStarted', { count: actions.length }), {
              description: t('leads.actions.proceedStartedHint'),
            });
          },
          onError: (err: Error) => toast.error(err.message || t('leads.actions.proceedFailed')),
        },
      );
    };

    const handleDelete = () => { void (async () => {
      const ok = await confirmAction({
        title: t('leads.deleteConfirmSoft'),
        confirmLabel: t('leads.confirmDelete'),
        cancelLabel: t('leads.cancelDelete'),
        tone: 'danger',
      });
      if (ok) deleteLead.mutate(lead.id, { onSuccess: () => navigate('/leads') });
    })(); };

    setOverride({
      title: lead.businessName,
      ...(sub ? { subtitle: sub } : {}),
      heroIcon: 'bolt-large',
      heroIconColor: 'var(--evo-steel)',
      bottomActions: (
        <PillGroup size="md">
          <ActionsDropdown
            lead={lead}
            hasAnalysis={lead.analysisStatus === 'completed'}
            updateStatusPending={updateStatus.isPending}
            runActionsPending={runLeadActions.isPending}
            onProceed={handleProceed}
            onMarkWon={() => updateStatus.mutate({ id: lead.id, status: 'closed_won' })}
            onMarkLost={() => updateStatus.mutate({ id: lead.id, status: 'closed_lost' })}
            onConvertToContact={() => {
              if (lead.contactId !== null) {
                navigate(`/contacts/${lead.contactId}`);
                return;
              }
              convertToContact.mutate({ id: lead.id }, {
                onSuccess: (result: ConvertResult) => {
                  if (result.action === 'created') {
                    toast.success(t('leads.detail.convertSuccess'));
                    navigate(`/contacts/${result.contactId}`);
                  } else if (result.action === 'existing') {
                    toast.info(t('leads.detail.existingSuccess'));
                    navigate(`/contacts/${result.contactId}`);
                  } else {
                    setConvertMatch({ contactId: result.contactId, email: lead.contactEmail ?? lead.ownerEmail ?? lead.scrapedEmail ?? '' });
                  }
                },
                onError: (err: Error) => toast.error(err.message),
              });
            }}
          />
          <RemoveDropdown
            lead={lead}
            updateStatusPending={updateStatus.isPending}
            reactivatePending={reactivateLead.isPending}
            onArchive={() => updateStatus.mutate({ id: lead.id, status: 'archived' })}
            onJunk={() => updateStatus.mutate({ id: lead.id, status: 'junk' })}
            onDelete={handleDelete}
            onReactivate={() => reactivateLead.mutate(lead.id, {
              onSuccess: () => toast.success(t('leads.actions.reactivateSuccess')),
              onError: (err: Error) => toast.error(err.message || t('leads.actions.reactivateFailed')),
            })}
          />
        </PillGroup>
      ),
    });
    return () => setOverride({});
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: .isPending booleans used instead of full mutation objects (unstable refs); navigate/confirmAction/deleteLead/toast/runLeadActions/reactivateLead are stable
  }, [lead, setOverride, t, analyze.isPending, updateStatus.isPending, generateDrafts.isPending, runLeadActions.isPending, reactivateLead.isPending, draftsForHeader]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-body-lg)' }}>
        {t('common.loading')}
      </div>
    );
  }

  if (!lead) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '12px' }}>
        <p style={{ color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-body-lg)' }}>{t('leads.noResults')}</p>
        <Button variant="link" tone="cyan" onClick={() => navigate('/leads')}>
          {t('leads.detail.back')}
        </Button>
      </div>
    );
  }

  const hasWebsite = !!lead.websiteUrl;
  const hasAnalysis = analysis?.analysisStatus === 'completed';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <SubNavBar
        lead={lead}
        analysis={analysis ?? null}
        tabs={[
          { value: 'info', label: t('leads.detail.tabs.info') },
          { value: 'outreach', label: t('leads.detail.tabs.outreach') },
          { value: 'projects', label: t('leads.detail.tabs.projects') },
          { value: 'reports', label: t('leads.detail.tabs.reports') },
        ]}
        activeTab={activeTab}
        onTabChange={(v) => setActiveTab(v as 'info' | 'outreach' | 'projects' | 'reports')}
      />

      {/* Main row: left (content) + right (Claude panel) */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Left: scrollable content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Scrollable content */}
          <div style={{ flex: 1, overflowY: 'auto' }}>

          {/* Tab: Información — 2-col grid */}
          {activeTab === 'info' && (
            <div style={{ padding: '0 var(--evo-content-pad-x)' }}>
              <div style={{ maxWidth: '1200px', paddingTop: 'var(--evo-space-6)' }}>
                <DetailSection
                  title={t('leads.information.title')}
                  titleRight={
                    <SectionHeaderAction
                      onClick={() => setNotesOpen(true)}
                    >
                      {t('leads.information.addNotes')}
                    </SectionHeaderAction>
                  }
                  divider={false}
                >
                  <div className="evo-info-grid">

                    {/* Left column */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
                      <Card header={<CardTitle>{t('leads.detail.businessSnapshot')}</CardTitle>} padding="sm">
                        <BusinessSnapshot lead={lead} />
                      </Card>
                      {hasAnalysis && analysis?.businessSummary && (
                        <Card header={<CardTitle>{t('leads.intelligence.summary_label')}</CardTitle>} padding="sm">
                          <SummarySection summary={analysis.businessSummary} />
                        </Card>
                      )}
                      {!hasAnalysis && (
                        <p style={{ fontSize: 'var(--evo-list-title-fs)', color: analyze.isError ? 'var(--evo-warn)' : 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', margin: 0 }}>
                          {analyze.isError
                            ? (analyze.error?.message === 'disqualified'
                                ? t('leads.intelligence.disqualified')
                                : t('leads.intelligence.failed'))
                            : analyze.isPending || analysis?.analysisStatus === 'analyzing'
                              ? t('leads.intelligence.analyzing')
                              : t('leads.intelligence.pending')}
                        </p>
                      )}
                      {hasAnalysis && analysis?.proposalAngle && (
                        <Card header={<CardTitle>{t('leads.intelligence.proposal_angle_label')}</CardTitle>} padding="none">
                          <ProposalAngleSection angle={analysis.proposalAngle} />
                        </Card>
                      )}
                      {lead.notes?.trim() && (
                        <Card header={<CardTitle>{t('leads.detail.notes.title')}</CardTitle>} padding="sm">
                          <NotesSection lead={lead} />
                        </Card>
                      )}
                    </div>

                    {/* Right column */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
                      {hasAnalysis && analysis?.opportunities && analysis.opportunities.length > 0 && (
                        <Card header={<CardTitle>{t('leads.intelligence.opportunities_label')} ({analysis.opportunities.length})</CardTitle>} padding="sm">
                          <OpportunitiesSection opportunities={analysis.opportunities} />
                        </Card>
                      )}
                      {hasAnalysis && analysis?.suggestedServices && analysis.suggestedServices.length > 0 && (
                        <Card header={<CardTitle>{t('leads.intelligence.suggested_services_label')}</CardTitle>} padding="sm">
                          <SuggestedServicesSection services={analysis.suggestedServices} />
                        </Card>
                      )}
                      <Card header={<CardTitle>{t('leads.detail.social.title')}</CardTitle>} padding="sm">
                        <SocialPresenceSection lead={lead} />
                      </Card>
                    </div>

                  </div>
                </DetailSection>
              </div>
            </div>
          )}

          {/* Tab: Outreach — full width */}
          {activeTab === 'outreach' && (
            <div style={{ padding: '0 var(--evo-content-pad-x)' }}>
              <OutreachSection lead={lead} />
            </div>
          )}

          {/* Tab: Proyectos */}
          {activeTab === 'projects' && (
            <div style={{ padding: '0 var(--evo-content-pad-x)' }}>
              <LeadProjectsTab lead={lead} />
            </div>
          )}

          {/* Tab: Reports — full width */}
          {activeTab === 'reports' && (
            <div style={{ padding: '0 var(--evo-content-pad-x)' }}>
              <LeadReportsBlock leadId={lead.id} leadName={lead.businessName} websiteUrl={lead.websiteUrl} />
            </div>
          )}

          </div>
        </div>{/* end left column */}

        <LeadClaudePanel lead={lead} analysis={analysis ?? null} />
      </div>{/* end main row */}

      {confirmDialog}
      <NotesModal lead={lead} open={notesOpen} onClose={() => setNotesOpen(false)} />

      {convertMatch && (
        <Modal
          open={!!convertMatch}
          onClose={() => setConvertMatch(null)}
          title={t('leads.detail.convertModal.title')}
          size="sm"
          footer={
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Button variant="ghost" tone="muted" size="sm" onClick={() => setConvertMatch(null)}>
                {t('leads.detail.convertModal.cancel')}
              </Button>
              <Button
                variant="outline"
                tone="muted"
                size="sm"
                loading={convertToContact.isPending}
                disabled={linkToContact.isPending}
                onClick={() => {
                  convertToContact.mutate({ id: lead.id, forceNew: true }, {
                    onSuccess: (result) => {
                      setConvertMatch(null);
                      toast.success(t('leads.detail.convertSuccess'));
                      navigate(`/contacts/${result.contactId}`);
                    },
                    onError: (err: Error) => toast.error(err.message),
                  });
                }}
              >
                {t('leads.detail.convertModal.createNew')}
              </Button>
              <Button
                variant="solid"
                tone="cyan"
                size="sm"
                loading={linkToContact.isPending}
                disabled={convertToContact.isPending}
                onClick={() => {
                  linkToContact.mutate(
                    { id: lead.id, contactId: convertMatch.contactId },
                    {
                      onSuccess: () => {
                        toast.success(t('leads.detail.linkSuccess'));
                        navigate(`/contacts/${convertMatch.contactId}`);
                        setConvertMatch(null);
                      },
                      onError: (err: Error) => toast.error(err.message),
                    },
                  );
                }}
              >
                {t('leads.detail.convertModal.link')}
              </Button>
            </div>
          }
        >
          <p style={{ margin: 0, fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)' }}>
            {t('leads.detail.convertModal.body', { email: convertMatch.email })}
          </p>
        </Modal>
      )}
    </div>
  );
}
